import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, Check, MessageSquare, RefreshCw, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfWeek, isSameDay, isBefore, addMinutes, parseISO } from "date-fns";
import { useAuditLog } from "@/hooks/useAuditLog";
import {
  getBrowserTimezone,
  getTimezoneAbbr,
  formatInTz,
  generateSlotsForDay,
} from "@/lib/timezone";
import { formatInTimeZone } from "date-fns-tz";

interface AvailabilitySlot {
  provider_id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string;
  end_time: string;
}

interface Appointment {
  start_time: string;
  end_time: string;
  provider_id: string;
}

interface ProviderProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  title_prefix?: string | null;
  specialty?: string | null;
  video_room_url?: string | null;
}

const SLOT_DURATION = 15; // minutes

const BookAppointment = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [existingAppointments, setExistingAppointments] = useState<Appointment[]>([]);
  const [providers, setProviders] = useState<ProviderProfile[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [activeAppointment, setActiveAppointment] = useState<{ id: string; start_time: string; end_time: string; call_type: string; meeting_url: string | null; video_required?: boolean } | null>(null);
  const [providerRequiresVideo, setProviderRequiresVideo] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [confirmedCalUrls, setConfirmedCalUrls] = useState<{ google: string; ics: string } | null>(null);
  const [providerTimezone, setProviderTimezone] = useState("America/New_York");
  const [patientState, setPatientState] = useState<string | null>(null);
  const [stateBlocked, setStateBlocked] = useState(false);
  const [providerCredentials, setProviderCredentials] = useState<{ license_state: string; specialty: string | null } | null>(null);
  const { logAccess } = useAuditLog();

  const patientTz = useMemo(() => getBrowserTimezone(), []);
  const patientTzAbbr = useMemo(() => getTimezoneAbbr(patientTz), [patientTz]);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  useEffect(() => {
    fetchProviders();
    fetchActiveAppointment();
  }, [user]);

  useEffect(() => {
    if (selectedProvider) {
      fetchProviderTimezone();
      fetchAvailability();
      fetchExistingAppointments();
    }
  }, [selectedProvider, weekStart]);

  const fetchProviders = async () => {
    if (!user) return;

    const { data: profile } = await supabase
      .from("profiles")
      .select("assigned_provider_id, state")
      .eq("user_id", user.id)
      .single();

    setPatientState(profile?.state ?? null);

    const { data: profiles } = await supabase
      .from("provider_profiles_public" as any)
      .select("user_id, first_name, last_name, title_prefix, specialty, video_room_url");

    if (profiles && profiles.length > 0) {
      setProviders(profiles as any);
      const assignedId = profile?.assigned_provider_id;
      const match = assignedId && profiles.find((p: any) => p.user_id === assignedId);

      if (match) {
        setSelectedProvider((match as any).user_id);
      } else {
        const { data: availProviders } = await supabase
          .from("provider_availability")
          .select("provider_id")
          .in("provider_id", profiles.map((p: any) => p.user_id));

        const idsWithAvail = new Set((availProviders || []).map((a) => a.provider_id));
        const preferred = profiles.find((p: any) => idsWithAvail.has(p.user_id));
        setSelectedProvider(preferred ? (preferred as any).user_id : (profiles[0] as any).user_id);
      }
    }

    // PRD v3.0 — state compliance check
    if (profile?.state) {
      const { data: licResp } = await supabase.functions.invoke("check-state-provider", { body: { state: profile.state } });
      const licensed = !!(licResp && (licResp as { licensed?: boolean }).licensed);
      setStateBlocked(!licensed);
      logAccess({
        action: "state_licensure_check",
        resource_type: "state_check",
        metadata: { state: profile.state, licensed: !!licensed },
      });
    }
  };

  const fetchProviderTimezone = async () => {
    if (!selectedProvider) return;
    const { data } = await supabase
      .from("profiles")
      .select("timezone")
      .eq("user_id", selectedProvider)
      .single();
    if (data?.timezone) setProviderTimezone(data.timezone);
  };

  const fetchAvailability = async () => {
    if (!selectedProvider) return;
    const { data } = await supabase
      .from("provider_availability")
      .select("provider_id, day_of_week, specific_date, start_time, end_time")
      .eq("provider_id", selectedProvider);
    if (data) setAvailability(data as AvailabilitySlot[]);
  };

  const fetchExistingAppointments = async () => {
    if (!selectedProvider) return;
    const weekEnd = addDays(weekStart, 7);
    const { data } = await supabase
      .from("appointments")
      .select("start_time, end_time, provider_id")
      .eq("provider_id", selectedProvider)
      .gte("start_time", weekStart.toISOString())
      .lt("start_time", weekEnd.toISOString())
      .neq("status", "cancelled");
    if (data) setExistingAppointments(data as Appointment[]);
  };

  const fetchActiveAppointment = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("appointments")
      .select("id, start_time, end_time, call_type, meeting_url, video_required")
      .eq("patient_id", user.id)
      .in("status", ["scheduled", "confirmed"])
      .gte("end_time", new Date().toISOString())
      .order("start_time", { ascending: true })
      .limit(1);
    setActiveAppointment(data && data.length > 0 ? data[0] as any : null);

    // Detect provider's "video required" decision recorded on the latest assessment.
    const { data: latestAssessment } = await supabase
      .from("health_assessments")
      .select("status")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1);
    setProviderRequiresVideo(latestAssessment?.[0]?.status === "video_required");
  };

  /** Get available slots for a calendar day, using provider timezone */
  const getSlotsForDay = (date: Date): Date[] => {
    const dayOfWeek = date.getDay();
    // Format date in provider's timezone to get the correct date string
    const dateStr = formatInTimeZone(date, providerTimezone, "yyyy-MM-dd");
    
    // Match availability windows — specific_date overrides day_of_week
    const specificDateWindows = availability.filter((a) => a.specific_date === dateStr);
    const dayAvailability = specificDateWindows.length > 0
      ? specificDateWindows
      : availability.filter((a) => !a.specific_date && a.day_of_week === dayOfWeek);

    // Build set of already-booked start times (as UTC milliseconds)
    const bookedStarts = new Set(
      existingAppointments
        .filter((appt) => isSameDay(parseISO(appt.start_time), date))
        .map((appt) => parseISO(appt.start_time).getTime())
    );

    const rawSlots = generateSlotsForDay(
      format(date, "yyyy-MM-dd"),
      providerTimezone,
      dayAvailability,
      SLOT_DURATION,
      bookedStarts,
    );

    // Dedupe overlapping windows and sort chronologically
    const seen = new Set<number>();
    return rawSlots
      .filter((s) => {
        const t = s.getTime();
        if (seen.has(t)) return false;
        seen.add(t);
        return true;
      })
      .sort((a, b) => a.getTime() - b.getTime());
  };

  const firstAvailableDate = useMemo(
    () => weekDays.find((day) => getSlotsForDay(day).length > 0) ?? null,
    [weekDays, availability, existingAppointments, providerTimezone]
  );

  useEffect(() => {
    if (!selectedProvider) return;

    if (!firstAvailableDate) {
      if (selectedDate || selectedSlot) {
        setSelectedDate(null);
        setSelectedSlot(null);
      }
      return;
    }

    const selectedDateInWeek = selectedDate
      ? weekDays.some((day) => isSameDay(day, selectedDate))
      : false;
    const selectedDateHasSlots = selectedDate
      ? getSlotsForDay(selectedDate).length > 0
      : false;

    if (!selectedDate || !selectedDateInWeek || !selectedDateHasSlots) {
      setSelectedDate(firstAvailableDate);
      setSelectedSlot(null);
    }
  }, [selectedProvider, firstAvailableDate, selectedDate, selectedSlot, weekDays, availability, existingAppointments, providerTimezone]);

  const hasAvailability = (date: Date) => {
    const dayOfWeek = date.getDay();
    const dateStr = format(date, "yyyy-MM-dd");
    const hasSpecific = availability.some((a) => a.specific_date === dateStr);
    if (hasSpecific) return true;
    return availability.some((a) => !a.specific_date && a.day_of_week === dayOfWeek);
  };

  // Default consults are async via the in-app secure inbox (HIPAA-compliant over Supabase RLS).
  // If the selected provider has published a BAA-covered live-video room URL
  // (Doxy.me / Daily.co / Zoom for Healthcare — validated by DB trigger), use that instead.
  const getConsultUrl = () => {
    const provider = providers.find((p) => p.user_id === selectedProvider);
    const room = provider?.video_room_url?.trim();
    if (room) return room;
    return `${window.location.origin}/dashboard?tab=messages`;
  };

  const isVideoConsultUrl = (url: string) => !url.includes("/dashboard?tab=messages");

  const buildEventCopy = (slot: Date, providerName: string, providerSpecialty: string | null, consultUrl: string) => {
    const isVideo = isVideoConsultUrl(consultUrl);
    const providerLine = providerSpecialty
      ? `Provider: ${providerName} (${providerSpecialty})`
      : `Provider: ${providerName || "Your Provider"}`;
    const timeDisplay = `${formatInTz(slot, patientTz, "h:mm a")} – ${formatInTz(addMinutes(slot, SLOT_DURATION), patientTz, "h:mm a")} ${patientTzAbbr}`;
    const headline = isVideo
      ? `Live video consultation via Elara Health`
      : `Secure async check-in via Elara Health`;
    const ctaLine = isVideo
      ? `Join your secure video room at the scheduled time:`
      : `Open your secure inbox to message your provider:`;
    const lines = [
      headline,
      ``,
      providerLine,
      `Date: ${formatInTz(slot, patientTz, "EEEE, MMMM d, yyyy")}`,
      `Time: ${timeDisplay}`,
      ``,
      ctaLine,
      consultUrl,
    ];
    const title = isVideo
      ? `Elara Health Video Consultation${providerName ? ` with ${providerName}` : ""}`
      : `Elara Health Check-in${providerName ? ` with ${providerName}` : ""}`;
    return { lines, title };
  };

  const buildGoogleCalendarUrl = (slot: Date, providerName: string, providerSpecialty: string | null, consultUrl: string) => {
    const start = slot.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const end = addMinutes(slot, SLOT_DURATION).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const { lines, title } = buildEventCopy(slot, providerName, providerSpecialty, consultUrl);
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: title,
      dates: `${start}/${end}`,
      details: lines.join("\n"),
      location: consultUrl,
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const buildIcsDataUrl = (slot: Date, providerName: string, providerSpecialty: string | null, consultUrl: string) => {
    const pad = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const dtStart = pad(slot);
    const dtEnd = pad(addMinutes(slot, SLOT_DURATION));
    const { lines, title } = buildEventCopy(slot, providerName, providerSpecialty, consultUrl);
    const desc = lines.join("\\n");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Elara Health//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${desc}`,
      `LOCATION:${consultUrl}`,
      `URL:${consultUrl}`,
      `STATUS:CONFIRMED`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
  };

  const bookAppointment = async () => {
    if (!user || !selectedSlot || !selectedProvider) return;
    setIsBooking(true);

    const wasReschedule = !!activeAppointment;
    if (activeAppointment) {
      await supabase
        .from("appointments")
        .update({ status: "rescheduled" })
        .eq("id", activeAppointment.id);
    }

    const consultUrl = getConsultUrl();
    const isVideo = !consultUrl.includes("/dashboard?tab=messages");
    const callType = isVideo ? "video" : "async";
    const { error } = await supabase.from("appointments").insert({
      patient_id: user.id,
      provider_id: selectedProvider,
      start_time: selectedSlot.toISOString(),
      end_time: addMinutes(selectedSlot, SLOT_DURATION).toISOString(),
      meeting_url: consultUrl,
      call_type: callType,
      video_required: providerRequiresVideo || isVideo,
    } as any);
    if (error) {
      toast({ title: "Booking failed", description: error.message, variant: "destructive" });
    } else {
      setConfirmed(true);
      setIsRescheduling(false);
      fetchExistingAppointments();
      fetchActiveAppointment();
      logAccess({ action: "book_appointment", resource_type: "appointment", metadata: { provider_id: selectedProvider, start_time: selectedSlot.toISOString() } });
      toast({
        title: "Check-in scheduled!",
        description: `${formatInTz(selectedSlot, patientTz, "EEEE, MMMM d")} at ${formatInTz(selectedSlot, patientTz, "h:mm a")} ${patientTzAbbr}`,
      });

      const providerName = selectedProviderProfile
        ? `${(selectedProviderProfile as any).title_prefix ? (selectedProviderProfile as any).title_prefix + " " : ""}${selectedProviderProfile.last_name || selectedProviderProfile.first_name || "Provider"}`
        : "Your Provider";
      const providerSpecialty = (selectedProviderProfile as any)?.specialty || null;
      const appointmentDate = formatInTz(selectedSlot, patientTz, "EEEE, MMMM d, yyyy");
      const appointmentTime = `${formatInTz(selectedSlot, patientTz, "h:mm a")} – ${formatInTz(addMinutes(selectedSlot, SLOT_DURATION), patientTz, "h:mm a")} ${patientTzAbbr}`;
      const googleCalUrl = buildGoogleCalendarUrl(selectedSlot, providerName, providerSpecialty, consultUrl);
      const icsUrl = buildIcsDataUrl(selectedSlot, providerName, providerSpecialty, consultUrl);
      setConfirmedCalUrls({ google: googleCalUrl, ics: icsUrl });
      try {
        // Skip the generic "appointment-booked" patient email when this is a
        // reschedule — the patient gets the dedicated "appointment-rescheduled"
        // confirmation below instead, so they don't receive two emails at once.
        if (!wasReschedule) {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-booked",
              recipientUserId: user.id,
              idempotencyKey: `appt-booked-patient-${selectedSlot.toISOString()}-${selectedProvider}`,
              notificationCategory: "appointment_reminders",
              templateData: {
                recipientRole: "patient",
                otherPartyName: providerName,
                appointmentDate,
                appointmentTime,
                callType,
                meetingUrl: consultUrl,
                googleCalendarUrl: googleCalUrl,
                icsUrl,
                timezone: patientTzAbbr,
              },
            },
          });
        }

        const { data: patientProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("user_id", user.id)
          .single();
        const patientName = patientProfile
          ? `${patientProfile.first_name || ""} ${patientProfile.last_name || ""}`.trim() || "Patient"
          : "Patient";

        const providerTzAbbr = getTimezoneAbbr(providerTimezone);
        const providerApptDate = formatInTz(selectedSlot, providerTimezone, "EEEE, MMMM d, yyyy");
        const providerApptTime = `${formatInTz(selectedSlot, providerTimezone, "h:mm a")} – ${formatInTz(addMinutes(selectedSlot, SLOT_DURATION), providerTimezone, "h:mm a")} ${providerTzAbbr}`;

        if (wasReschedule) {
          // On a reschedule, send the provider a dedicated rescheduled-confirmation
          // email (with the new slot) instead of the generic appointment-booked.
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-rescheduled",
              recipientUserId: selectedProvider,
              idempotencyKey: `appt-rescheduled-confirm-provider-${selectedSlot.toISOString()}-${selectedProvider}`,
              notificationCategory: "appointment_reminders",
              templateData: {
                recipientName: selectedProviderProfile?.first_name || providerName,
                patientName,
                rescheduledBy: "patient",
                newDate: providerApptDate,
                newTime: providerApptTime,
                meetingUrl: consultUrl,
                callType,
                googleCalendarUrl: googleCalUrl,
                icsUrl,
                appointmentTitle: `Consultation with ${patientName}`,
              },
            },
          });
        } else {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-booked",
              recipientUserId: selectedProvider,
              idempotencyKey: `appt-booked-provider-${selectedSlot.toISOString()}-${selectedProvider}`,
              notificationCategory: "appointment_reminders",
              templateData: {
                recipientRole: "provider",
                otherPartyName: patientName,
                appointmentDate: providerApptDate,
                appointmentTime: providerApptTime,
                callType,
                meetingUrl: consultUrl,
                googleCalendarUrl: googleCalUrl,
                icsUrl,
                timezone: providerTzAbbr,
              },
            },
          });
        }

        // If this booking was the result of a patient-initiated reschedule,
        // also send the patient an explicit "rescheduled to" confirmation
        // so both sides have a clear record of the new appointment time.
        if (wasReschedule) {
          const patientFirstName = patientProfile?.first_name || "there";
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-rescheduled",
              recipientUserId: user.id,
              idempotencyKey: `appt-rescheduled-confirm-patient-${selectedSlot.toISOString()}-${selectedProvider}`,
              notificationCategory: "appointment_reminders",
              templateData: {
                recipientName: patientFirstName,
                providerName,
              rescheduledBy: "patient",
                newDate: appointmentDate,
                newTime: appointmentTime,
                meetingUrl: consultUrl,
                callType,
                googleCalendarUrl: googleCalUrl,
                icsUrl,
                appointmentTitle: `Consultation with ${providerName}`,
              },
            },
          });
        }
      } catch (err) {
        console.error("Failed to send booking confirmation emails:", err);
      }
    }
    setIsBooking(false);
  };

  const selectedProviderProfile = providers.find((p) => p.user_id === selectedProvider);

  // Gate: patient already has an active appointment
  if (!confirmed && !isRescheduling && activeAppointment) {
    return (
      <Card className="border-border bg-card max-w-md mx-auto">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <CalendarDays size={28} className="text-primary" />
          </div>
          <h2 className="font-display text-2xl text-foreground mb-2">You have an appointment</h2>
          <p className="text-muted-foreground mb-4">You already have a consultation scheduled.</p>
          <div className="p-4 rounded-lg bg-muted/50 space-y-1 text-sm">
            <p className="font-medium text-foreground">{formatInTz(activeAppointment.start_time, patientTz, "EEEE, MMMM d, yyyy")}</p>
            <p className="text-muted-foreground">
              {formatInTz(activeAppointment.start_time, patientTz, "h:mm a")} – {formatInTz(activeAppointment.end_time, patientTz, "h:mm a")} {patientTzAbbr}
            </p>
          </div>
          <Button
            variant="outline"
            className="mt-6 gap-1.5"
            onClick={() => setIsRescheduling(true)}
          >
            <RefreshCw size={14} /> Reschedule
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (confirmed && selectedSlot) {
    return (
      <Card className="border-border bg-card max-w-md mx-auto">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Check size={28} className="text-primary" />
          </div>
          <h2 className="font-display text-2xl text-foreground mb-2">
            {isRescheduling ? "Rescheduled!" : "You're booked!"}
          </h2>
          <p className="text-muted-foreground mb-4">Your consultation is confirmed.</p>
          <div className="p-4 rounded-lg bg-muted/50 space-y-1 text-sm">
            <p className="font-medium text-foreground">{formatInTz(selectedSlot, patientTz, "EEEE, MMMM d, yyyy")}</p>
            <p className="text-muted-foreground">
              {formatInTz(selectedSlot, patientTz, "h:mm a")} – {formatInTz(addMinutes(selectedSlot, SLOT_DURATION), patientTz, "h:mm a")} {patientTzAbbr}
            </p>
            <p className="text-muted-foreground text-xs italic">Your provider details have been sent to your email.</p>
          </div>
          {confirmedCalUrls && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <a
                href={confirmedCalUrls.google}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <CalendarDays size={14} /> Add to Google Calendar
              </a>
              <a
                href={confirmedCalUrls.ics}
                download="elara-consultation.ics"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <CalendarDays size={14} /> Download iCal (.ics)
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CalendarDays size={20} className="text-primary" />
        <h2 className="font-display text-xl text-foreground">Book a Consultation</h2>
      </div>

      {providers.length === 0 && (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center text-muted-foreground">
            <CalendarDays size={40} className="mx-auto mb-3 opacity-50" />
            <p>No providers have set up availability yet. Check back soon!</p>
          </CardContent>
        </Card>
      )}

      {selectedProvider && (
        <>
          {providerRequiresVideo && (
            <Card className="border-primary/40 bg-primary/5">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <MessageSquare size={14} className="text-primary" />
                </div>
                <div className="text-sm">
                  <p className="font-medium text-foreground">Your provider has requested a live video consultation</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Please book a slot below. Your provider will share a secure HIPAA-compliant video room link before your visit.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timezone indicator */}
          <p className="text-xs text-muted-foreground">
            Times shown in your local timezone ({patientTzAbbr})
          </p>

          {/* Week navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setWeekStart((d) => addDays(d, -7))}>
              <ChevronLeft size={18} />
            </Button>
            <p className="text-sm font-medium text-foreground">
              {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
            </p>
            <Button variant="ghost" size="icon" onClick={() => setWeekStart((d) => addDays(d, 7))}>
              <ChevronRight size={18} />
            </Button>
          </div>

          {/* Day selector */}
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day) => {
              const available = hasAvailability(day);
              const isPast = isBefore(day, new Date()) && !isSameDay(day, new Date());
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              return (
                <button
                  key={day.toISOString()}
                  disabled={!available || isPast}
                  onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                  className={`p-3 rounded-lg text-center transition-all ${
                    isSelected
                      ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : available && !isPast
                        ? "bg-card border border-border hover:border-primary/50 cursor-pointer"
                        : "bg-muted/30 text-muted-foreground/50 cursor-not-allowed"
                  }`}
                >
                  <p className="text-xs font-medium">{format(day, "EEE")}</p>
                  <p className="text-lg font-bold">{format(day, "d")}</p>
                </button>
              );
            })}
          </div>

          {/* No availability fallback */}
          {!firstAvailableDate && (
            <Card className="border-border bg-card">
              <CardContent className="p-8 text-center text-muted-foreground">
                <Clock size={36} className="mx-auto mb-3 opacity-50" />
                <p className="font-medium text-foreground mb-1">No availability this week</p>
                <p className="text-sm">This provider doesn't have any open slots for the current week. Try navigating to the next week.</p>
              </CardContent>
            </Card>
          )}

          {/* Time slots */}
          {selectedDate && (
            <Card className="border-border bg-card">
              <CardContent className="p-5">
                <p className="text-sm font-medium text-foreground mb-3">
                  <Clock size={14} className="inline mr-1.5" />
                  Available times for {format(selectedDate, "EEEE, MMMM d")}
                </p>
                {(() => {
                  const daySlots = getSlotsForDay(selectedDate);
                  if (daySlots.length === 0) {
                    return <p className="text-sm text-muted-foreground italic">No available slots for this day.</p>;
                  }
                  return (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {daySlots.map((slot) => {
                        const isSelected = selectedSlot && slot.getTime() === selectedSlot.getTime();
                        return (
                          <button
                            key={slot.toISOString()}
                            onClick={() => setSelectedSlot(slot)}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted/50 text-foreground hover:bg-accent hover:text-accent-foreground"
                            }`}
                          >
                            {formatInTz(slot, patientTz, "h:mm a")}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Confirm */}
          {selectedSlot && (() => {
            const consultUrl = getConsultUrl();
            const isVideo = !consultUrl.includes(window.location.host);
            return (
            <div className="space-y-4">
              <Card className="border-border bg-card">
                <CardContent className="p-5 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {isVideo ? <Video size={16} className="text-primary" /> : <MessageSquare size={16} className="text-primary" />}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">
                      {isVideo ? "Live video consultation" : "Secure async check-in"}
                    </p>
                    <p>
                      {isVideo
                        ? "At your scheduled time, you'll join your provider's HIPAA-compliant video room. The link is included in your confirmation email and on your dashboard."
                        : "At your scheduled time, your provider will review your intake and respond in your secure inbox. No video or phone call required — everything stays inside Elara Health."}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {formatInTz(selectedSlot, patientTz, "EEEE, MMMM d")} at {formatInTz(selectedSlot, patientTz, "h:mm a")} {patientTzAbbr}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isVideo ? "15-minute live video consultation" : "15-minute async check-in via secure messaging"}
                    </p>
                  </div>
                  <Button onClick={bookAppointment} disabled={isBooking} className="gap-1.5">
                    {isBooking ? (isRescheduling ? "Rescheduling..." : "Booking...") : (isRescheduling ? "Confirm Reschedule" : "Confirm Booking")}
                    {!isBooking && (isRescheduling ? <RefreshCw size={14} /> : <Check size={14} />)}
                  </Button>
                </CardContent>
              </Card>
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default BookAppointment;
